/**
 * planeReferenceLineTools — three subclasses of Cornerstone3D's ReferenceLinesTool,
 * one per anatomical plane, so each can carry its own toolName, sourceViewportId,
 * and color style.
 *
 * Cornerstone's built-in ReferenceLines only supports one source viewport per tool
 * group. To get the full 3-plane crosshair (axial intersection on coronal+sagittal,
 * coronal on axial+sagittal, sagittal on axial+coronal) we register the tool three
 * times under different names. Per-toolName color is configured in viewportToolSetup
 * via annotation.config.style.setToolGroupToolStyles.
 *
 * Each subclass also wraps renderAnnotation in a try/catch. handleFullDimension can
 * throw during the early-load window when a target viewport is in the engine but
 * doesn't yet have a volume (either getTargetId throws, or getTargetImageData
 * crashes on the undefined id). Any uncaught throw in renderAnnotation aborts the
 * annotation render pass for every tool, so the crosshair never paints. Swallowing
 * is safe — once volumes load the next render tick draws correctly.
 *
 * Factory pattern (same as BumpContourTool / RegionGrowTool) keeps cornerstone-tools
 * imports lazy so tree-shaking and our async loader continue to work.
 *
 * UNIFIED TRUE-GEOMETRY CROSSHAIR (#575): a reference line is
 * `source plane ∩ target plane`, projected through the TARGET camera. The base
 * tool builds the SOURCE plane from `getViewportImageCornersInWorld`, which reads
 * the source viewport's render camera — cardinal-snapped on the GPU-volume render
 * (`snapViewportCameraToCardinal`), true-IOP on the CPU-stack render — so the
 * crosshairs diverge between strategies. We instead rebuild the source plane from
 * the source image's true DICOM `imagePlaneModule` (IOP/IPP/spacing), which is
 * identical regardless of any camera snap. Both strategies then draw the same,
 * geometrically-true lines, while the volume keeps its upright display (its
 * camera snap is left untouched — only the crosshair source plane is decoupled).
 * See `imagePlaneCornersFromModule` + `installTrueSourcePlane`.
 */

import { getCornerstone } from "../utils/dicomLoader";
import { logger } from "../utils/logger";

export const REFERENCE_LINES_AXIAL = "ReferenceLinesAxial";
export const REFERENCE_LINES_CORONAL = "ReferenceLinesCoronal";
export const REFERENCE_LINES_SAGITTAL = "ReferenceLinesSagittal";

export type PlaneReferenceLinesToolName =
  | typeof REFERENCE_LINES_AXIAL
  | typeof REFERENCE_LINES_CORONAL
  | typeof REFERENCE_LINES_SAGITTAL;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeSafe(instance: any) {
  const orig = instance.renderAnnotation;
  if (typeof orig !== "function") return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  instance.renderAnnotation = function (...args: any[]) {
    try {
      return orig.apply(this, args);
    } catch {
      return false;
    }
  };
}

/** Intersect an infinite line through (p, q) in canvas space with the four
 *  edges of a (0,0)→(w,h) rectangle. Returns the two intersection points
 *  in canvas coords, or null if the line doesn't meaningfully cross the rect.
 */
function clipLineToCanvas(
  p: [number, number],
  q: [number, number],
  w: number,
  h: number,
): [[number, number], [number, number]] | null {
  const [x1, y1] = p;
  const [x2, y2] = q;
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return null;
  // Parameterize point = p + t * (q - p). Find t for each canvas edge.
  const ts: number[] = [];
  if (Math.abs(dx) > 1e-9) {
    ts.push((0 - x1) / dx);
    ts.push((w - x1) / dx);
  }
  if (Math.abs(dy) > 1e-9) {
    ts.push((0 - y1) / dy);
    ts.push((h - y1) / dy);
  }
  // Keep t values whose resulting point lies on the rect boundary.
  const hits: [number, number][] = [];
  const epsilon = 1e-6;
  for (const t of ts) {
    const x = x1 + t * dx;
    const y = y1 + t * dy;
    if (x >= -epsilon && x <= w + epsilon && y >= -epsilon && y <= h + epsilon) {
      hits.push([x, y]);
    }
  }
  if (hits.length < 2) return null;
  // Deduplicate corner hits (a line through a corner shows up twice).
  hits.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const unique: [number, number][] = [hits[0]];
  for (let i = 1; i < hits.length; i++) {
    const prev = unique[unique.length - 1];
    if (Math.abs(hits[i][0] - prev[0]) > 0.5 || Math.abs(hits[i][1] - prev[1]) > 0.5) {
      unique.push(hits[i]);
    }
  }
  if (unique.length < 2) return null;
  return [unique[0], unique[unique.length - 1]];
}

/** Replace ReferenceLinesTool's image-bounded full-dimension behavior with
 *  a canvas-bounded one so cross-hair lines reach the actual viewport edges
 *  regardless of zoom/pan or image extent on screen.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function installFullCanvasExtension(instance: any) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  instance.handleFullDimension = function (
    targetViewport: any,
    lineStartWorld: number[],
    _viewPlaneNormal: number[],
    _viewUp: number[],
    lineEndWorld: number[],
    fallback: [number, number][],
  ): [number, number][] {
    try {
      const startCanvas = targetViewport.worldToCanvas(lineStartWorld) as [number, number];
      const endCanvas = targetViewport.worldToCanvas(lineEndWorld) as [number, number];
      const element = targetViewport.element as HTMLElement | undefined;
      const w = element?.clientWidth ?? 0;
      const h = element?.clientHeight ?? 0;
      if (w <= 0 || h <= 0) return fallback;
      const clipped = clipLineToCanvas(startCanvas, endCanvas, w, h);
      return clipped ?? fallback;
    } catch {
      return fallback;
    }
  };
}

// ─── True-geometry source plane (unified crosshair, #575) ───────────────────
type Vec3 = [number, number, number];

/** The subset of Cornerstone's `imagePlaneModule` this needs. Either the 6-cosine
 *  `imageOrientationPatient` OR the split `rowCosines`/`columnCosines` is accepted,
 *  and either `row/columnPixelSpacing` OR `pixelSpacing` ([rowSpacing, colSpacing]). */
export interface ImagePlaneModuleLike {
  imageOrientationPatient?: number[] | null;
  rowCosines?: number[] | null;
  columnCosines?: number[] | null;
  imagePositionPatient?: number[] | null;
  rows?: number | null;
  columns?: number | null;
  rowPixelSpacing?: number | null;
  columnPixelSpacing?: number | null;
  pixelSpacing?: number[] | null;
}

/**
 * Build the 4 world corners of an image plane from its true DICOM geometry —
 * independent of any viewport camera (and therefore of the volume path's
 * cardinal-snap). Order matches what the base ReferenceLinesTool consumes:
 * `[topLeft, topRight, bottomLeft, bottomRight]`.
 *
 *   topLeft     = IPP
 *   topRight    = IPP + rowCosines · (columns · columnPixelSpacing)
 *   bottomLeft  = IPP + columnCosines · (rows · rowPixelSpacing)
 *   bottomRight = topRight + columnCosines · (rows · rowPixelSpacing)
 *
 * Returns `null` when any field is missing/degenerate (caller keeps the base
 * camera-derived corners). Pure — unit-testable without Cornerstone.
 */
export function imagePlaneCornersFromModule(
  ipm: ImagePlaneModuleLike | null | undefined,
): Vec3[] | null {
  if (!ipm) return null;
  const iop = ipm.imageOrientationPatient;
  const rowCos = ipm.rowCosines ?? (iop && iop.length >= 6 ? iop.slice(0, 3) : null);
  const colCos = ipm.columnCosines ?? (iop && iop.length >= 6 ? iop.slice(3, 6) : null);
  const ipp = ipm.imagePositionPatient;
  const rows = ipm.rows;
  const cols = ipm.columns;
  const rowSpacing = ipm.rowPixelSpacing ?? ipm.pixelSpacing?.[0];
  const colSpacing = ipm.columnPixelSpacing ?? ipm.pixelSpacing?.[1];

  if (!rowCos || rowCos.length < 3 || !colCos || colCos.length < 3) return null;
  if (!ipp || ipp.length < 3) return null;
  if (typeof rows !== "number" || typeof cols !== "number" || rows <= 0 || cols <= 0) return null;
  if (
    typeof rowSpacing !== "number" ||
    typeof colSpacing !== "number" ||
    rowSpacing <= 0 ||
    colSpacing <= 0
  ) {
    return null;
  }

  const w = cols * colSpacing; // horizontal extent, along rowCosines
  const h = rows * rowSpacing; // vertical extent, along columnCosines
  const tl: Vec3 = [ipp[0], ipp[1], ipp[2]];
  const tr: Vec3 = [tl[0] + rowCos[0] * w, tl[1] + rowCos[1] * w, tl[2] + rowCos[2] * w];
  const bl: Vec3 = [tl[0] + colCos[0] * h, tl[1] + colCos[1] * h, tl[2] + colCos[2] * h];
  const br: Vec3 = [tr[0] + colCos[0] * h, tr[1] + colCos[1] * h, tr[2] + colCos[2] * h];
  return [tl, tr, bl, br];
}

/**
 * Wrap a tool instance's `_init` so the SOURCE plane is rebuilt from the source
 * image's true DICOM `imagePlaneModule` instead of the render camera. Same
 * instance-method wrapping pattern as `makeSafe` / `installFullCanvasExtension`.
 *
 * The base `_init` sets `editData.annotation.data.handles.points` from the source
 * viewport's camera corners (cardinal on the snapped volume, true on the stack).
 * We overwrite them with the true acquired corners, so the crosshair is identical
 * on both strategies. Only `handles.points` drives the line direction in our
 * render path (target camera handles the projection); metadata is left as-is.
 * Best-effort: any failure leaves the base corners in place.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function installTrueSourcePlane(instance: any) {
  const origInit = instance._init;
  if (typeof origInit !== "function") return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  instance._init = function (...args: any[]) {
    const result = origInit.apply(this, args);
    try {
      const ed = this.editData;
      const pts = ed?.annotation?.data?.handles?.points;
      if (!pts) return result;
      const vp = ed.renderingEngine?.getViewport?.(ed.sourceViewportId);
      const imageId = vp?.getCurrentImageId?.();
      if (!imageId) return result;
      const ipm = getCornerstone().metaData.get(
        "imagePlaneModule",
        imageId,
      ) as ImagePlaneModuleLike | null;
      const corners = imagePlaneCornersFromModule(ipm);
      if (corners) ed.annotation.data.handles.points = corners;
    } catch (err) {
      // The expected early-load / missing-metadata cases already returned above
      // (the imageId guard + the `if (corners)` guard handle a not-yet-hydrated
      // imagePlaneModule). Reaching here means an UNEXPECTED failure — e.g. a
      // Cornerstone API rename or an unforeseen viewport shape — which would
      // silently revert the crosshair to the pre-fix camera-derived (drifted)
      // corners. Fall back, but surface it once per instance so a persistent
      // regression is observable instead of silently wrong (see repo "no silent
      // execution" rule). Once-per-instance gate keeps the per-camera-modified
      // _init from spamming.
      if (!this.__trueSourcePlaneWarned) {
        this.__trueSourcePlaneWarned = true;
        logger.warn(
          "[planeReferenceLineTools] installTrueSourcePlane: could not source crosshair from imagePlaneModule for %s — falling back to camera-derived corners",
          this.getToolName?.() ?? "ReferenceLines",
          err,
        );
      }
    }
    return result;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createPlaneReferenceLineToolClasses(cornerstoneTools: any) {
  const Base = cornerstoneTools.ReferenceLinesTool;

  class ReferenceLinesAxial extends Base {
    static toolName = REFERENCE_LINES_AXIAL;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(...args: any[]) {
      super(...args);
      makeSafe(this);
      installFullCanvasExtension(this);
      installTrueSourcePlane(this);
    }
  }
  class ReferenceLinesCoronal extends Base {
    static toolName = REFERENCE_LINES_CORONAL;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(...args: any[]) {
      super(...args);
      makeSafe(this);
      installFullCanvasExtension(this);
      installTrueSourcePlane(this);
    }
  }
  class ReferenceLinesSagittal extends Base {
    static toolName = REFERENCE_LINES_SAGITTAL;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(...args: any[]) {
      super(...args);
      makeSafe(this);
      installFullCanvasExtension(this);
      installTrueSourcePlane(this);
    }
  }

  return { ReferenceLinesAxial, ReferenceLinesCoronal, ReferenceLinesSagittal };
}
